import { useCallback, useEffect, useRef, useState } from 'react';

import authRequest from '../../../../utils/fetch';
import type { CodeReviewerSetting, CodeReviewerSettingValue } from '../types';

import { URL } from './url';

type GithubRepoListResponse = {
	repositories: GH.Repository[];
};
type GithubRepositoryList = {
	repoList: GH.Repository[];
	loading: boolean;
	error: string | null;
	searchRepos: (searchString: string) => void;
	updateRepoSettingInList: (repoId: string, newValue: boolean) => void;
};
type UseGithubRepositoryListProps = {
	subscriptionId: string;
	page: number;
	rowsPerPage: number;
};
type RequestBody = {
	orgSubscriptionId: string;
	page: number;
	pageLength: number;
	reposNameToSearch?: string;
};
export const useGithubRepositoryList = ({
	subscriptionId,
	page,
	rowsPerPage,
}: UseGithubRepositoryListProps): GithubRepositoryList => {
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const rowCache = useRef<Map<number, GH.Repository[]>>(new Map());
	const [repoList, setRepoList] = useState<GH.Repository[]>([]);

	const createAuthRequest = useCallback(
		(pageNumber: number, searchString?: string) => {
			const body: RequestBody = {
				orgSubscriptionId: subscriptionId,
				page: pageNumber,
				pageLength: rowsPerPage,
			};

			if (searchString) {
				body.reposNameToSearch = searchString;
			}

			return authRequest(URL('getRepositories'), {
				method: 'POST',
				body: JSON.stringify(body),
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			});
		},
		[rowsPerPage, subscriptionId],
	);

	const tryRequest = useCallback(
		async (request: Request, cacheResult: boolean, pageNumber?: number) => {
			setLoading(true);
			try {
				const res = await fetch(request);
				if (!res.ok) {
					throw new Error(`http error status ${res.status} with text: ${res.statusText}`);
				}
				const response = (await res.json()) as GithubRepoListResponse;
				// Set the cache with the value
				if (cacheResult && pageNumber) {
					rowCache.current.set(pageNumber, response.repositories);
				}
				setRepoList(response.repositories);
			} catch (e) {
				setError('An error has occurred');
			}
			setLoading(false);
		},
		[],
	);

	const getRepoList = useCallback(
		async (pageNumber: number) => {
			const cachedValue = rowCache.current.get(pageNumber);

			// check if the current page is in the rowCache
			if (cachedValue) {
				setRepoList(cachedValue);
				return;
			}

			// Otherwise fetch this page
			const request = createAuthRequest(pageNumber);
			await tryRequest(request, true, pageNumber);
		},
		[createAuthRequest, tryRequest],
	);

	const searchRepos = useCallback(
		async (searchString: string) => {
			const request = createAuthRequest(1, searchString);
			await tryRequest(request, false);
		},
		[createAuthRequest, tryRequest],
	);

	useEffect(() => {
		getRepoList(page);
	}, [getRepoList, page]);

	const updateRepoSettingInList = (repoId: string, newValue: boolean) => {
		// Update the repo in the current list
		setRepoList((current) =>
			current.map((repo) => {
				if (repo.repoId === repoId) {
					return {
						...repo,
						autoreviewSettings: {
							...repo.autoreviewSettings,
							value: { autoreview_enabled: newValue } as CodeReviewerSettingValue,
						} as CodeReviewerSetting,
					};
				}
				return repo;
			}),
		);

		// Update the repo in the cache
		rowCache.current.set(
			page, // is this confirmed that the current page will be on screen?
			rowCache.current.get(page)?.map((repo) => {
				if (repo.repoId === repoId) {
					return {
						...repo,
						autoreviewSettings: {
							...repo.autoreviewSettings,
							value: { autoreview_enabled: newValue } as CodeReviewerSettingValue,
						} as CodeReviewerSetting,
					};
				}
				return repo;
			}) ?? [],
		);
	};

	return {
		repoList,
		loading,
		error,
		searchRepos,
		updateRepoSettingInList,
	};
};
